import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Snackbar,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useApi } from '../api';
import { usePrinters } from '../hooks/usePrinters';
import { useCurrentUser } from '../auth';
import { PRINTER_REGISTRATION_CODE_CREATE_PERMISSION, PRINTER_DELETE_PERMISSION } from '../constants/permissions';
import { PrinterCard, type Printer } from '../components/printers';
import { ContinuousPrinting } from '../components/printing';
import type { Product } from '../components/products';
import { StatementXmlPreview } from '../components/statement-nodes';
import PageHeader from '../components/PageHeader';

interface TranslationSummary {
  head_id: string;
  language: string;
  latest_version: number;
  updated_at: string;
  latest_text?: string | null;
}

type SnackbarState = {
  open: boolean;
  message: string;
  severity: 'success' | 'error';
};

const DEFAULT_SNACKBAR: SnackbarState = { open: false, message: '', severity: 'success' };

const Printing: React.FC = () => {
  const { t } = useTranslation();
  const api = useApi();
  const {
    printers,
    loading: printersLoading,
    error: printersError,
    registrationCode,
    creatingCode,
    refresh,
    requestRegistrationCode,
    clearRegistrationCode,
    deletePrinter,
  } = usePrinters();
  const { permissions } = useCurrentUser();
  const canGenerateRegistrationCode = useMemo(
    () => (permissions || []).includes(PRINTER_REGISTRATION_CODE_CREATE_PERMISSION),
    [permissions]
  );
  const canDeletePrinter = useMemo(
    () => (permissions || []).includes(PRINTER_DELETE_PERMISSION),
    [permissions]
  );

  const [activeTab, setActiveTab] = useState<'print' | 'continuous' | 'connect'>('continuous');
  const [label, setLabel] = useState('');
  const [expiresMinutes, setExpiresMinutes] = useState(60);

  const [eanInput, setEanInput] = useState('');
  const [product, setProduct] = useState<Product | null>(null);
  const [translations, setTranslations] = useState<TranslationSummary[]>([]);
  const [selectedPrinterId, setSelectedPrinterId] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [copies, setCopies] = useState(1);
  const [loadingProduct, setLoadingProduct] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>(DEFAULT_SNACKBAR);

  useEffect(() => {
    if (printers.length === 0) {
      setSelectedPrinterId('');
      return;
    }
    if (!printers.find((item) => item.id === selectedPrinterId)) {
      setSelectedPrinterId(printers[0].id);
    }
  }, [printers, selectedPrinterId]);

  const handleTabChange = (_event: React.SyntheticEvent, value: string) => {
    setActiveTab(value as 'print' | 'continuous' | 'connect');
  };

  const fetchProductByEan = useCallback(
    async (ean: string) => {
      try {
        setLoadingProduct(true);
        const productData: Product = await api.get(`/resolver?ean=${encodeURIComponent(ean)}`);
        setProduct(productData);
        const translationData: TranslationSummary[] = await api.get(
          `/products/${productData.id}/translations?includeText=true`
        );
        setTranslations(translationData);
        const preferredLanguage =
          translationData.find((item) => item.language === productData.base_language)?.language ||
          translationData[0]?.language ||
          '';
        setSelectedLanguage(preferredLanguage);
        setSnackbar({
          open: true,
          severity: 'success',
          message: t('printing.productLoaded'),
        });
      } catch (err) {
        console.error('Unable to resolve product by EAN', err);
        setProduct(null);
        setTranslations([]);
        setSnackbar({
          open: true,
          severity: 'error',
          message: t('printing.productNotFound'),
        });
      } finally {
        setLoadingProduct(false);
      }
    },
    [api, t]
  );

  const handleEanSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = eanInput.trim();
    if (!trimmed) {
      setSnackbar({ open: true, severity: 'error', message: t('printing.eanRequired') });
      return;
    }
    await fetchProductByEan(trimmed);
  };

  const handleLanguageChange = (event: SelectChangeEvent<string>) => {
    setSelectedLanguage(event.target.value);
  };

  const handlePrinterChange = (event: SelectChangeEvent<string>) => {
    setSelectedPrinterId(event.target.value);
  };

  const selectedTranslation = useMemo(
    () => translations.find((item) => item.language === selectedLanguage),
    [translations, selectedLanguage]
  );

  const handlePrint = async () => {
    if (!product || !selectedPrinterId) {
      setSnackbar({ open: true, severity: 'error', message: t('printing.printerOrProductMissing') });
      return;
    }
    const labelLanguage = selectedLanguage || product.base_language || 'en';
    const payload = {
      product_id: product.id,
      language: labelLanguage,
      copies: Math.min(Math.max(copies, 1), 10),
      correlation_id: `ui-${Date.now()}`,
    };

    try {
      setPrinting(true);
      await api.post(`/printers/${selectedPrinterId}/jobs/label`, payload);
      setSnackbar({ open: true, severity: 'success', message: t('printing.printingSuccess') });
    } catch (err) {
      console.error('Failed to create print job', err);
      setSnackbar({ open: true, severity: 'error', message: t('printing.printingFailure') });
    } finally {
      setPrinting(false);
    }
  };

  const handleCopyCode = async () => {
    if (!registrationCode?.code) return;
    try {
      await navigator.clipboard.writeText(registrationCode.code);
      setSnackbar({ open: true, severity: 'success', message: t('printing.registrationCodeCopied') });
    } catch (err) {
      console.error('Clipboard write failed', err);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const renderConnectTab = () => (
    <Grid container spacing={3}>
      {canGenerateRegistrationCode && (
      <Grid item xs={12} md={5}>
        <Card elevation={0} sx={{ border: (theme) => '1px solid ' + theme.palette.divider, borderRadius: 2 }}>
          <CardContent>
            <Stack spacing={3}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('printing.generateTitle')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('printing.instructions')}
                </Typography>
              </Box>
              <TextField
                label={t('printing.labelField')}
                placeholder={t('printing.labelPlaceholder')}
                value={label}
                onChange={(event) => setLabel(event.target.value)}
                fullWidth
              />
              <TextField
                label={t('printing.expiresField')}
                type="number"
                inputProps={{ min: 5, max: 1440 }}
                value={expiresMinutes}
                onChange={(event) => {
                  const value = Number(event.target.value);
                  setExpiresMinutes(Number.isNaN(value) ? 60 : value);
                }}
                fullWidth
              />
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  onClick={() => requestRegistrationCode(label, expiresMinutes)}
                  disabled={creatingCode}
                >
                  {creatingCode ? t('printing.generating') : t('printing.generateButton')}
                </Button>
                {registrationCode && (
                  <Button variant="text" onClick={clearRegistrationCode}>
                    {t('printing.clearCode')}
                  </Button>
                )}
              </Stack>
              {registrationCode && (
                <Box sx={{ p: 2, borderRadius: 2, border: (theme) => '1px solid ' + theme.palette.divider }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {t('printing.registrationCodeTitle')}
                      </Typography>
                      <Typography variant="h4" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                        {registrationCode.code}
                      </Typography>
                      {registrationCode.expires_at && (
                        <Typography variant="caption" color="text.secondary">
                          {t('printing.codeExpires', {
                            value: new Date(registrationCode.expires_at).toLocaleString(),
                          })}
                        </Typography>
                      )}
                    </Box>
                    <Tooltip title={t('printing.copyCode')}>
                      <IconButton onClick={handleCopyCode}>
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Grid>
      )}
      <Grid item xs={12} md={canGenerateRegistrationCode ? 7 : 12}>
        <Card elevation={0} sx={{ border: (theme) => '1px solid ' + theme.palette.divider, borderRadius: 2 }}>
          <CardContent>
            <Stack spacing={3}>
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('printing.connectedTitle')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('printing.connectedSubtitle')}
                  </Typography>
                </Box>
                <Button variant="outlined" onClick={() => refresh()} disabled={printersLoading}>
                  {t('printing.refresh')}
                </Button>
              </Stack>

              {printersError && <Alert severity="error">{printersError}</Alert>}

              {printersLoading ? (
                <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : printers.length === 0 ? (
                <Alert severity="info">{t('printing.connectedEmpty')}</Alert>
              ) : (
                <Grid container spacing={2}>
                  {printers.map((printer: Printer) => (
                    <Grid item xs={12} sm={6} key={printer.id}>
                      <PrinterCard printer={printer} onDelete={canDeletePrinter ? deletePrinter : undefined} />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderPrintTab = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={5}>
        <Card elevation={0} sx={{ border: (theme) => '1px solid ' + theme.palette.divider, borderRadius: 2 }}>
          <CardContent>
            <Stack spacing={3}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('printing.scanTitle')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('printing.scanInstructions')}
                </Typography>
              </Box>
              <Box component="form" onSubmit={handleEanSubmit}>
                <Stack spacing={2}>
                  <TextField
                    label={t('printing.eanLabel')}
                    placeholder={t('printing.eanPlaceholder')}
                    value={eanInput}
                    onChange={(event) => setEanInput(event.target.value)}
                    autoFocus
                    fullWidth
                  />
                  <Button type="submit" variant="contained" disabled={loadingProduct}>
                    {loadingProduct ? t('printing.searching') : t('printing.searchButton')}
                  </Button>
                </Stack>
              </Box>
              {product && (
                <Box>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    {t('printing.productDetails')}
                  </Typography>
                  <Stack spacing={1}>
                    <Typography variant="body2">
                      <strong>{t('printing.productName')}:</strong> {product.name}
                    </Typography>
                    {product.sku && (
                      <Typography variant="body2">
                        <strong>{t('printing.productSku')}:</strong> {product.sku}
                      </Typography>
                    )}
                    {(product.ean || eanInput) && (
                      <Typography variant="body2">
                        <strong>{t('printing.productEan')}:</strong> {product.ean || eanInput}
                      </Typography>
                    )}
                    {product.base_language && (
                      <Typography variant="body2">
                        <strong>{t('printing.baseLanguage')}:</strong> {product.base_language.toUpperCase()}
                      </Typography>
                    )}
                  </Stack>
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={7}>
        <Card elevation={0} sx={{ border: (theme) => '1px solid ' + theme.palette.divider, borderRadius: 2 }}>
          <CardContent>
            <Stack spacing={3}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <FormControl fullWidth>
                  <InputLabel id="printer-select-label">{t('printing.printerSelectLabel')}</InputLabel>
                  <Select
                    labelId="printer-select-label"
                    value={selectedPrinterId}
                    label={t('printing.printerSelectLabel')}
                    onChange={handlePrinterChange}
                  >
                    {printers.map((printer) => (
                      <MenuItem value={printer.id} key={printer.id}>
                        {printer.display_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel id="language-select-label">{t('printing.translationSelectLabel')}</InputLabel>
                  <Select
                    labelId="language-select-label"
                    value={selectedLanguage}
                    label={t('printing.translationSelectLabel')}
                    onChange={handleLanguageChange}
                    disabled={translations.length === 0}
                  >
                    {translations.map((translation) => (
                      <MenuItem value={translation.language} key={translation.head_id}>
                        {translation.language.toUpperCase()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label={t('printing.copiesLabel')}
                  type="number"
                  inputProps={{ min: 1, max: 10 }}
                  value={copies}
                  onChange={(event) => {
                    const value = Number(event.target.value);
                    setCopies(Number.isNaN(value) ? 1 : value);
                  }}
                  fullWidth
                />
              </Stack>

              {translations.length === 0 && product && (
                <Alert severity="warning">{t('printing.noTranslations')}</Alert>
              )}

              {selectedTranslation?.latest_text && (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    {t('printing.translationPreview')}
                  </Typography>
                  <Box
                    sx={{
                      borderRadius: 2,
                      border: (theme) => '1px solid ' + theme.palette.divider,
                      backgroundColor: 'background.paper',
                      p: 2,
                      minHeight: 120,
                    }}
                  >
                    <StatementXmlPreview value={selectedTranslation.latest_text} />
                  </Box>
                </Box>
              )}

              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  onClick={handlePrint}
                  disabled={printing || !product || !selectedPrinterId}
                >
                  {printing ? t('printing.printingInProgress') : t('printing.printButton')}
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  return (
    <Box>
      <PageHeader title={t('printing.title')} subtitle={t('printing.subtitle')} />

      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab value="continuous" label={t('printing.continuous.tab')} />
        <Tab value="print" label={t('printing.printTab')} />
        <Tab value="connect" label={t('printing.connectTab')} />
      </Tabs>

      {activeTab === 'print' && renderPrintTab()}
      {activeTab === 'continuous' && (
        <ContinuousPrinting printers={printers} onShowSnackbar={showSnackbar} />
      )}
      {activeTab === 'connect' && renderConnectTab()}

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleSnackbarClose}>
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Printing;
